import type { CofQuizQuestion } from '@tunasong/music-lib'
import { Button } from '@tunasong/ui-lib'
import type { FC } from 'react'
import styles from './cof-signature-answer.module.css'

export interface CofSignatureAnswerProps {
  question: Extract<CofQuizQuestion, { kind: 'count-signature' }>
  interactive: boolean
  onAnswer: (picked: { kind: 'signature'; count: number; direction: 'sharp' | 'flat' }) => void
}

const COUNTS = [0, 1, 2, 3, 4, 5, 6, 7] as const

/**
 * CoF Wheel v1.5 signature-counter answer surface — two rows of 8 buttons
 * (sharps + flats). The 0♯ ≡ 0♭ equivalence is resolved in the analyzer's
 * validator, not here; this surface just dispatches the clicked count +
 * direction.
 *
 * Spec: docs/superpowers/specs/2026-05-28-cof-wheel-v1.5-design.md §6.2
 */
export const CofSignatureAnswer: FC<CofSignatureAnswerProps> = props => {
  const { interactive, onAnswer } = props
  return (
    <div className={styles.panel}>
      <div className={styles.row} role="group" aria-label="Sharp signatures">
        <span className={styles.rowLabel}>Sharps</span>
        {COUNTS.map(n => (
          <Button
            key={`sharp-${n}`}
            type="button"
            variant="outlined"
            size="small"
            className={styles.btn}
            disabled={!interactive}
            onClick={() => onAnswer({ kind: 'signature', count: n, direction: 'sharp' })}
          >
            {n}♯
          </Button>
        ))}
      </div>
      <div className={styles.row} role="group" aria-label="Flat signatures">
        <span className={styles.rowLabel}>Flats</span>
        {COUNTS.map(n => (
          <Button
            key={`flat-${n}`}
            type="button"
            variant="outlined"
            size="small"
            className={styles.btn}
            disabled={!interactive}
            onClick={() => onAnswer({ kind: 'signature', count: n, direction: 'flat' })}
          >
            {n}♭
          </Button>
        ))}
      </div>
    </div>
  )
}
